// Admin events (ADMIN_P0 §2; the names are the contract): what the app reports for the back office.
//   track(kind, data)      queue an app event. Sent to POST /api/events in batches of at most 50: every ~30 s, when the
//                          app goes to the background and when the page hides (fetch keepalive). Signed in only; each
//                          event goes out under the account it was recorded under, never another one.
//   deviceId()             the random id this device keeps (localStorage 'xm.device'). Onboarding steps carry it
//                          (screens/onboarding/draft.ts obStep) and so does register, which links them to the account.
//   installAnalytics()     once at boot, before initNative() (a notification tap can cold-start the app): app_open /
//                          app_bg from the app's comings and goings, push_open from notification taps (native.ts
//                          'note-open'), and the flushes. The small window (K2) only sends what its buttons track.
// The data types (api/types.ts AppEventData) take ids, numbers and fixed values only: never chat text, card titles,
// memory text or anything the user typed. Until the server has the endpoint it answers 404: events are dropped
// silently, with nothing on screen, in the β trail or in the sign-out on 401 (api/client.ts `background`).
import type { AppEvent, AppEventData, AppEventKind } from '../api/types';
import { api } from '../api/client';
import { getToken, safeStorage } from '../api/token';
import { isDesktopShell, isMiniWindow, onNative } from '../native';

const BATCH = 50;               // the server takes at most 50 per call
const MAX_QUEUE = 200;          // beyond this the oldest go
const MAX_BATCHES = 4;          // per flush (keepalive bodies share a 64 KB budget)
const EVERY_MS = 30_000;
const SETTLE_MS = 2_500;        // after the app comes up, wait this long for the notification tap that brought it up
const TAP_BEFORE_MS = 3_000;    // a tap this long before the app came up still counts as the reason
const DESK_GRACE_MS = 30_000;   // desktop shell: back within this long after switching to another app = the same visit

// ------------------------------------------------------------------ device id
const DEVICE_KEY = 'xm.device';
let device: string | null = null;

function randomId(): string {
  try {
    const b = new Uint8Array(12);
    crypto.getRandomValues(b);
    return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  } catch {
    return (Math.random().toString(36).slice(2) + Date.now().toString(36)).slice(0, 24);
  }
}

/** This device's random id ('d_' + 24 hex), kept in localStorage; for this page only when storage is blocked. */
export function deviceId(): string {
  if (device) return device;
  const saved = safeStorage.get('local', DEVICE_KEY);
  device = saved && /^[\w-]{8,64}$/.test(saved) ? saved : 'd_' + randomId();
  if (device !== saved) safeStorage.set('local', DEVICE_KEY, device);
  return device;
}

// ------------------------------------------------------------------ queue
interface Queued { ev: AppEvent; tok: string }
let queue: Queued[] = [];

/** A guard against mistakes: strings cut short, numbers finite, nothing undefined. */
function clean<T extends object>(data: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(data)) {
    if (v === undefined) continue;
    out[k] = typeof v === 'string' ? v.slice(0, 100) : typeof v === 'number' ? (Number.isFinite(v) ? v : 0) : v;
  }
  return out as T;
}

/** Queue one app event (`at`: when it happened, this device's clock). Nothing happens while signed out. */
export function track<K extends AppEventKind>(kind: K, data: AppEventData[K], at: number = Date.now()): void {
  const tok = getToken();
  if (!tok) return;
  const ev: AppEvent<K> = { kind, at: Math.round(at), data: clean(data) };
  queue.push({ ev, tok });
  if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
}

/** Send what is queued (fire and forget; what fails is dropped). */
export function flush(): void {
  catchUp();
  for (let n = 0; n < MAX_BATCHES && queue.length; n++) {
    const tok = queue[0].tok;
    let k = 0;
    while (k < queue.length && k < BATCH && queue[k].tok === tok) k++;
    const batch = queue.splice(0, k);
    void api.events({ events: batch.map((q) => q.ev) }, tok);
  }
}

/** What is still waiting (tests, the mock debug handle). */
export const pending = (): AppEvent[] => queue.map((q) => q.ev);

/** Minutes with one decimal (cowork_end, o9_done). */
export const minutesOf = (secs: number) => Math.round(Math.max(0, secs) / 6) / 10;
/** Minutes on a Working together timer (desktop): from the card's start to `now` (both the server's clock); 0 when
 *  that can't be right (not started, ahead of now, over 12 hours). */
export function timerMinutes(startedAt: string | null | undefined, now: number): number {
  const ms = startedAt ? now - Date.parse(startedAt) : 0;
  return minutesOf(ms > 0 && ms < 12 * 3600_000 ? ms / 1000 : 0);
}

// ------------------------------------------------------------------ visits: app_open / app_bg / push_open
let fg = false;             // a visit is going on
let since = 0;              // when the app last came to the front during it
let acc = 0;                // ms in front during it, before `since`
let openAt = 0;             // when it began
let openPending = false;    // its app_open waits for a notification tap (SETTLE_MS)
let openTimer: ReturnType<typeof setTimeout> | null = null;
let bgTimer: ReturnType<typeof setTimeout> | null = null;   // desktop: switched away, not long enough yet
let launched = false;       // this page reported its first visit: later ones are 'resume'
let lastTap: { id: string; at: number } | null = null;
let nativeActive = true;    // native.ts 'pause' / 'resume' (Capacitor appStateChange, page visibility)
/** The visit's app_open while nobody was signed in (onboarding, the sign-in page): sent once someone is, if that
 *  happens before the visit ends, so the visit's app_bg never goes alone. */
let openLater: { data: AppEventData['app_open']; at: number } | null = null;

function catchUp() {
  if (!openLater || !getToken()) return;
  const o = openLater;
  openLater = null;
  track('app_open', o.data, o.at);
}

function inFront(): boolean {
  if (document.visibilityState === 'hidden' || !nativeActive) return false;
  // The desktop shell's main window stays 'visible' while it is hidden in the tray (no background throttling), so
  // there "in front" means focused.
  return isDesktopShell() ? document.hasFocus() : true;
}

function reportOpen() {
  if (!openPending) return;
  openPending = false;
  if (openTimer) { clearTimeout(openTimer); openTimer = null; }
  const tap = lastTap && lastTap.at >= openAt - TAP_BEFORE_MS ? lastTap : null;
  lastTap = null;
  const data: AppEventData['app_open'] = tap ? { from: 'push', noteId: tap.id } : { from: launched ? 'resume' : 'launch' };
  launched = true;
  if (getToken()) track('app_open', data, openAt);
  else openLater = { data, at: openAt };
}

function endVisit() {
  if (bgTimer) { clearTimeout(bgTimer); bgTimer = null; }
  if (!fg) return;
  reportOpen();
  catchUp();
  openLater = null;
  fg = false;
  track('app_bg', { secs: Math.round(acc / 1000) });
  acc = 0;
  flush();
}

function check() {
  const now = Date.now();
  if (inFront()) {
    if (bgTimer) { clearTimeout(bgTimer); bgTimer = null; since = now; return; } // back in time: the same visit
    if (fg) return;
    fg = true; since = now; acc = 0; openAt = now; openPending = true;
    if (lastTap && lastTap.at >= now - TAP_BEFORE_MS) reportOpen();
    else openTimer = setTimeout(reportOpen, SETTLE_MS);
    return;
  }
  if (!fg || bgTimer) return;
  acc += now - since;
  if (isDesktopShell()) bgTimer = setTimeout(endVisit, DESK_GRACE_MS);
  else endVisit();
}

function onTap(id: string, kind: string) {
  track('push_open', { noteId: id, kind });
  if (fg && !openPending && !bgTimer) return; // the app was already in front: the tap opened nothing
  lastTap = { id, at: Date.now() };
  reportOpen(); // the visit it brought up may be waiting for its reason
}

function pageHide() {
  if (fg && !bgTimer) acc += Date.now() - since;
  endVisit();
  flush();
}

let installed = false;
export function installAnalytics(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  setInterval(flush, EVERY_MS);
  if (isMiniWindow()) {
    // The small window comes and goes with the main one: no visits here, only what its buttons track.
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(); });
    window.addEventListener('pagehide', () => flush());
    return;
  }
  onNative((e) => {
    if (e.type === 'note-open') onTap(e.id, e.kind);
    else if (e.type === 'pause') { nativeActive = false; check(); }
    else if (e.type === 'resume') { nativeActive = true; check(); }
  });
  document.addEventListener('visibilitychange', () => { check(); if (document.visibilityState === 'hidden') flush(); });
  window.addEventListener('focus', check);
  window.addEventListener('blur', check);
  window.addEventListener('pageshow', check);
  window.addEventListener('pagehide', pageHide);
  check();
}
